'use client';

import { useState, useEffect, useRef, memo } from 'react';
import { ExternalLink } from 'lucide-react';
import { dedupeLinkPreviews } from '@/lib/chat/display-filter';
interface LinkMetaItem {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

interface MessageWithLinks {
  id: string;
  content?: string;
  linkMeta?: LinkMetaItem[];
}

interface LinkMeta {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

const ogCache = new Map<string, LinkMeta | null>();
const inflight = new Map<string, Promise<LinkMeta | null>>();

async function fetchOG(url: string): Promise<LinkMeta | null> {
  if (ogCache.has(url)) return ogCache.get(url)!;
  if (inflight.has(url)) return inflight.get(url)!;

  const promise = (async () => {
    try {
      const res = await fetch(`/api/og?url=${encodeURIComponent(url)}`);
      if (!res.ok) { ogCache.set(url, null); return null; }
      const data = await res.json();
      const meta: LinkMeta = {
        url,
        title: data.title,
        description: data.description,
        image: data.imageLocal || data.image,
        siteName: data.siteName,
      };
      if (!meta.title && !meta.image) { ogCache.set(url, null); return null; }
      ogCache.set(url, meta);
      return meta;
    } catch {
      ogCache.set(url, null);
      return null;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, promise);
  return promise;
}

const URL_RE = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
const MAX_PREVIEWS = 3;

function extractUrls(content?: string): string[] {
  if (!content) return [];
  const matches = content.match(URL_RE);
  if (!matches) return [];
  return [...new Set(matches)].slice(0, MAX_PREVIEWS);
}

function getDomain(url: string): string {
  try { return new URL(url).hostname; } catch { return url; }
}

function PreviewCard({ meta }: { meta: LinkMeta }) {
  return (
    <a
      href={meta.url}
      target="_blank"
      rel="noreferrer"
      className="flex rounded-lg border border-border/60 bg-muted/30 overflow-hidden hover:bg-muted/50 transition-colors max-w-sm group"
    >
      <div className="w-1 shrink-0 bg-purple-500/60" />
      <div className="flex-1 min-w-0">
        <div className="px-3 py-2">
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <ExternalLink className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{meta.siteName || getDomain(meta.url)}</span>
          </div>
          {meta.title && (
            <div className="text-xs font-medium mt-0.5 line-clamp-2 group-hover:underline">{meta.title}</div>
          )}
          {meta.description && (
            <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{meta.description}</div>
          )}
        </div>
        {meta.image && (
          <div className="w-full overflow-hidden">
            <img src={meta.image} alt="" className="w-full h-auto max-h-48 object-cover" loading="lazy" />
          </div>
        )}
      </div>
    </a>
  );
}

function MessageLinkPreviewsInner({ message }: { message: MessageWithLinks }) {
  const urls = extractUrls(message.content);
  const [previews, setPreviews] = useState<LinkMeta[]>(() => {
    // Use server-provided linkMeta if available
    if (message.linkMeta && message.linkMeta.length > 0) {
      return message.linkMeta;
    }
    // Use cached OG data if available
    const cached: LinkMeta[] = [];
    for (const url of urls) {
      const meta = ogCache.get(url);
      if (meta) cached.push(meta);
    }
    return cached;
  });
  const ref = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(previews.length > 0);

  useEffect(() => {
    if (fetchedRef.current || urls.length === 0) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        fetchedRef.current = true;
        Promise.allSettled(urls.map(fetchOG)).then((results) => {
          const fetched: LinkMeta[] = [];
          for (const r of results) {
            if (r.status === 'fulfilled' && r.value) fetched.push(r.value);
          }
          if (fetched.length > 0) setPreviews(fetched);
        });
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [urls]);

  if (urls.length === 0) return null;

  // Defensive URL dedupe — a message can carry repeated linkMeta entries
  // (same URL surfaced from multiple sub-agent results, etc.). Without
  // dedupe the user sees N identical OG cards stacked.
  const uniquePreviews = dedupeLinkPreviews(previews);

  return (
    <div ref={ref} className="flex flex-col gap-1.5 mt-1">
      {uniquePreviews.map((meta) => (
        <PreviewCard key={meta.url} meta={meta} />
      ))}
    </div>
  );
}

export const MessageLinkPreviews = memo(MessageLinkPreviewsInner, (prev, next) => prev.message.id === next.message.id && prev.message.linkMeta === next.message.linkMeta) as React.MemoExoticComponent<(props: { message: MessageWithLinks }) => React.ReactElement | null>;
